import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import { IconMarketData, IconSearch } from '../components/Icons';
import { useAiQ } from '../state/AiQState'; // Import Shared State

const CURVE_TENORS = ['1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', '7Y', '10Y', '15Y', '20Y', '30Y'];
const BASE_RATES = [5.25, 5.30, 5.35, 5.10, 4.80, 4.60, 4.30, 4.25, 4.20, 4.30, 4.40, 4.35];

interface MarketObject {
    id: string;
    type: 'Yield Curve' | 'Vol Surface' | 'Equity' | 'Credit Curve';
    name: string;
    currency: string;
    lastUpdated: string;
}

const MarketDataView: React.FC = () => {
    const { watchlist, marketData } = useAiQ(); // Use Real Watchlist
    const [selectedObj, setSelectedObj] = useState<MarketObject | null>(null);
    const [shiftBps, setShiftBps] = useState(0);
    const [isEditMode, setIsEditMode] = useState(false);
    const [marketObjects, setMarketObjects] = useState<MarketObject[]>([]);

    // Transform Watchlist into Market Objects
    useEffect(() => {
        const coreObjects: MarketObject[] = [
            { id: 'YC-USD-OIS', type: 'Yield Curve', name: 'USD OIS 3M', currency: 'USD', lastUpdated: '14:05:22' },
            { id: 'YC-EUR-6M', type: 'Yield Curve', name: 'EUR EURIBOR 6M', currency: 'EUR', lastUpdated: '14:05:20' },
        ];

        const stockObjects: MarketObject[] = watchlist.map(ticker => ({
            id: `EQ-${ticker}`,
            type: 'Equity',
            name: `${ticker} Live Feed`,
            currency: 'USD',
            lastUpdated: 'Real-time'
        }));

        setMarketObjects([...coreObjects, ...stockObjects]);
        if (!selectedObj) setSelectedObj(coreObjects[0]);
    }, [watchlist]);

    // Generate Chart Data based on shift
    const chartData = CURVE_TENORS.map((tenor, i) => ({
        tenor,
        baseRate: BASE_RATES[i],
        shiftedRate: BASE_RATES[i] + (shiftBps / 100)
    }));

    const handleShift = (direction: 'up' | 'down') => {
        setShiftBps(prev => direction === 'up' ? prev + 10 : prev - 10);
    };

    return (
        <div className="flex h-full">
            {/* Left Panel: Object Browser */}
            <div className="w-72 bg-slate-50 border-r border-slate-200 flex flex-col">
                <div className="p-4 border-b border-slate-200">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-4">
                        <IconMarketData />
                        Market Data
                    </h2>
                    <div className="relative">
                        <input 
                            type="text" 
                            placeholder="Search Curves & Assets..." 
                            className="w-full pl-8 pr-3 py-2 bg-white border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <span className="absolute left-2.5 top-2.5 text-slate-400"><IconSearch /></span>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
                    {marketObjects.map(obj => (
                        <div 
                            key={obj.id}
                            onClick={() => { setSelectedObj(obj); setShiftBps(0); }}
                            className={`p-3 rounded cursor-pointer border transition-all ${
                                selectedObj?.id === obj.id 
                                ? 'bg-white border-blue-500 shadow-sm' 
                                : 'bg-transparent border-transparent hover:bg-slate-100'
                            }`}
                        >
                            <div className="flex justify-between items-center mb-1">
                                <span className="font-semibold text-sm text-slate-700">{obj.name}</span>
                                <span className="text-[10px] font-mono bg-slate-200 text-slate-600 px-1.5 rounded">{obj.currency}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500">{obj.type}</span>
                                <span className={`text-[10px] ${obj.type === 'Yield Curve' ? 'text-blue-500' : 'text-purple-500'}`}>
                                    {obj.id}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Panel: Visualization & Editor */}
            <div className="flex-1 flex flex-col overflow-hidden bg-white">
                {/* Toolbar */}
                <div className="h-14 border-b border-slate-200 flex items-center justify-between px-6 bg-white">
                    {selectedObj && (
                        <>
                        <div>
                            <h3 className="font-bold text-slate-800">{selectedObj.name}</h3>
                            <span className="text-xs text-slate-500">Last Updated: {selectedObj.lastUpdated} • Source: INTERNAL_FEED</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex bg-slate-100 rounded p-1">
                                <button 
                                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${!isEditMode ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
                                    onClick={() => setIsEditMode(false)}
                                >
                                    View
                                </button>
                                <button 
                                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${isEditMode ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}
                                    onClick={() => setIsEditMode(true)}
                                >
                                    Edit / Bump
                                </button>
                            </div>
                            <button className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded">
                                Save Changes
                            </button>
                        </div>
                        </>
                    )}
                </div>

                {/* Chart Area */}
                <div className="flex-1 p-6 overflow-y-auto custom-scrollbar">
                    {selectedObj?.type === 'Yield Curve' ? (
                        <div className="h-full flex flex-col">
                             <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm mb-6 flex-1 min-h-[400px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis dataKey="tenor" stroke="#94a3b8" fontSize={12} />
                                        <YAxis domain={['auto', 'auto']} stroke="#94a3b8" fontSize={12} unit="%" />
                                        <Tooltip 
                                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0' }}
                                            formatter={(val: number) => [`${val.toFixed(3)}%`, 'Rate']}
                                        />
                                        <Legend verticalAlign="top" height={36}/>
                                        <Line 
                                            name="Market Curve (Base)" 
                                            type="monotone" 
                                            dataKey="baseRate" 
                                            stroke="#64748b" 
                                            strokeWidth={2} 
                                            strokeDasharray="5 5" 
                                            dot={{r:3}}
                                        />
                                        <Line 
                                            name="Simulated Curve" 
                                            type="monotone" 
                                            dataKey="shiftedRate" 
                                            stroke="#2563eb" 
                                            strokeWidth={3} 
                                            dot={{r:4}}
                                            activeDot={{r:6}}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                             </div>

                             {/* Bump Controls */}
                             {isEditMode && (
                                 <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                                     <h4 className="text-sm font-bold text-slate-700 mb-4">Curve Scenario Analysis (Parallel Shift)</h4>
                                     <div className="flex items-center gap-6">
                                         <div className="flex items-center gap-2">
                                             <button onClick={() => handleShift('down')} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-300 hover:bg-slate-100 text-slate-600 font-bold text-lg">-</button>
                                             <div className="w-32 text-center">
                                                 <div className="text-2xl font-bold text-slate-800">{shiftBps > 0 ? '+' : ''}{shiftBps} bps</div>
                                                 <div className="text-xs text-slate-500">Shift Magnitude</div>
                                             </div>
                                             <button onClick={() => handleShift('up')} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-300 hover:bg-slate-100 text-slate-600 font-bold text-lg">+</button>
                                         </div>
                                         
                                         <div className="h-10 w-px bg-slate-300"></div>

                                         <div className="flex gap-4 text-sm">
                                             <div className="bg-white px-4 py-2 rounded border border-slate-200 shadow-sm">
                                                 <div className="text-slate-500 text-xs">PV01 Impact</div>
                                                 <div className={`font-mono font-bold ${shiftBps === 0 ? 'text-slate-700' : 'text-red-600'}`}>
                                                     {shiftBps === 0 ? '-' : `-$${(Math.abs(shiftBps) * 15200).toLocaleString()}`}
                                                 </div>
                                             </div>
                                             <div className="bg-white px-4 py-2 rounded border border-slate-200 shadow-sm">
                                                 <div className="text-slate-500 text-xs">Valuation Change</div>
                                                 <div className={`font-mono font-bold ${shiftBps === 0 ? 'text-slate-700' : 'text-red-600'}`}>
                                                     {shiftBps === 0 ? '-' : `-$${(Math.abs(shiftBps) * 125000).toLocaleString()}`}
                                                 </div>
                                             </div>
                                         </div>
                                     </div>
                                 </div>
                             )}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-slate-400 bg-slate-50/50 rounded-lg border-2 border-dashed border-slate-200">
                            <div className="text-center">
                                <div className="text-5xl mb-4 opacity-20"><IconMarketData /></div>
                                <p className="text-lg font-medium">Select a Yield Curve to analyze.</p>
                                {selectedObj?.type === 'Equity' && <p className="text-sm mt-2">Equity data for {selectedObj.name} is managed in the Portfolio Master.</p>}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MarketDataView;